import { useEffect, useState, useCallback, useMemo } from "react";
import MapboxDirections from "@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions";

import directionStyles from "./directionStyles";
import styles from "./styles.module.scss";

import "@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions.css";

// Calculate distance between two points using Haversine formula (returns km)
const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

// Copy coordinates to clipboard
const copyCoordinates = (lat, lng) => {
    const coords = `${lat}, ${lng}`;
    navigator.clipboard.writeText(coords).then(() => {
        const notification = document.createElement('div');
        notification.textContent = `Скопировано: ${coords}`;
        notification.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#333;color:#fff;padding:8px 16px;border-radius:6px;z-index:10000;font-size:14px;';
        document.body.appendChild(notification);
        setTimeout(() => notification.remove(), 2000);
    });
};

const createMarker = (mapboxgl, map, geo, className, label) => {
    const el = document.createElement("div");
    el.className = className;

    if (label) {
        el.innerHTML = `<span class="${styles.markerLabel}">${label}</span>`;
    }

    el.addEventListener('click', (e) => {
        e.stopPropagation();
        copyCoordinates(geo[1], geo[0]);
    });
    el.title = 'Нажмите, чтобы скопировать координаты';

    return new mapboxgl.Marker({
        element: el,
        anchor: 'center'
    }).setLngLat(geo).addTo(map.current);
};

// Fetch route from Mapbox Directions API and draw line
const fetchAndDrawRoute = async (mapboxgl, map, startCoords, endCoords, sourceId, layerId) => {
    try {
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${startCoords[0]},${startCoords[1]};${endCoords[0]},${endCoords[1]}?geometries=geojson&access_token=${mapboxgl.accessToken}`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.routes?.[0]) {
            map.current.addSource(sourceId, {
                type: 'geojson',
                data: { type: 'Feature', geometry: data.routes[0].geometry }
            });

            map.current.addLayer({
                id: layerId,
                type: 'line',
                source: sourceId,
                paint: { 'line-color': '#9ca3af', 'line-width': 3 }
            });
        }
    } catch (error) {
        console.error('Failed to fetch route:', error);
    }
};

const MIN_DISTANCE_KM = 10;

const Direction = ({ mapboxgl, map, setDirectionInfo, points, searchCoordinates }) => {
    const [directions, setDirections] = useState(null);

    const originPoint = useMemo(() => points.find((p) => p.name === "uploading"), [points]);
    const destinationPoint = useMemo(() => points.find((p) => p.name === "unloading"), [points]);

    const uploadingPointsGeo = useMemo(() =>
        points.reduce((acc, point) => {
            if (point.name === "uploading" && point.ln !== originPoint.ln && point.lat !== originPoint.lat) {
                acc.push([Number(point.ln), Number(point.lat)]);
            }
            return acc;
        }, []),
    [points, originPoint]);

    const unloadingPointsGeo = useMemo(() =>
        points.reduce((acc, point) => {
            if (point.name === "unloading" && point.ln !== destinationPoint.ln && point.lat !== destinationPoint.lat) {
                acc.push([Number(point.ln), Number(point.lat)]);
            }
            return acc;
        }, []),
    [points, destinationPoint]);

    const handleMapLoad = useCallback(() => {
        directions
            .setOrigin([originPoint.ln, originPoint.lat])
            .setDestination([destinationPoint.ln, destinationPoint.lat]);

        createMarker(mapboxgl, map, [Number(originPoint.ln), Number(originPoint.lat)], `${styles.marker} ${styles.marker__origin}`, 'A');
        createMarker(mapboxgl, map, [Number(destinationPoint.ln), Number(destinationPoint.lat)], `${styles.marker} ${styles.marker__destination}`, 'B');

        uploadingPointsGeo.forEach((point, index) => {
            directions.addWaypoint(index, point);
            createMarker(mapboxgl, map, point, `${styles.marker} ${styles.marker__uploading}`);
        });

        unloadingPointsGeo.forEach((point, index) => {
            directions.setWaypoint(index, point);
            createMarker(mapboxgl, map, point, `${styles.marker} ${styles.marker__unloading}`);
        });

        // Search coordinates markers and routes (only if distance > 10km)
        if (searchCoordinates) {
            const { loadingFrom, loadingTo } = searchCoordinates;

            if (loadingFrom?.lat && loadingFrom?.ln) {
                const searchCoords = [Number(loadingFrom.ln), Number(loadingFrom.lat)];
                const actualCoords = [Number(originPoint.ln), Number(originPoint.lat)];
                const distance = getDistanceKm(loadingFrom.lat, loadingFrom.ln, Number(originPoint.lat), Number(originPoint.ln));

                if (distance >= MIN_DISTANCE_KM) {
                    createMarker(mapboxgl, map, searchCoords, `${styles.marker} ${styles.marker__search}`);
                    fetchAndDrawRoute(mapboxgl, map, searchCoords, actualCoords, 'search-loading-route', 'search-loading-route-layer');
                }
            }

            if (loadingTo?.lat && loadingTo?.ln) {
                const searchCoords = [Number(loadingTo.ln), Number(loadingTo.lat)];
                const actualCoords = [Number(destinationPoint.ln), Number(destinationPoint.lat)];
                const distance = getDistanceKm(loadingTo.lat, loadingTo.ln, Number(destinationPoint.lat), Number(destinationPoint.ln));

                if (distance >= MIN_DISTANCE_KM) {
                    createMarker(mapboxgl, map, searchCoords, `${styles.marker} ${styles.marker__search}`);
                    fetchAndDrawRoute(mapboxgl, map, searchCoords, actualCoords, 'search-unloading-route', 'search-unloading-route-layer');
                }
            }
        }

        map.current.addControl(directions, "top-left");
    }, [directions, map, mapboxgl, destinationPoint, originPoint, unloadingPointsGeo, uploadingPointsGeo, searchCoordinates]);

    const handleDirectionRoute = useCallback(({ route }) => {
        setDirectionInfo({
            totalDistance: route[0]?.distance,
            totalTime: route[0]?.duration,
        });

        const bounds = new mapboxgl.LngLatBounds(
            [originPoint.ln, originPoint.lat],
            [destinationPoint.ln, destinationPoint.lat]
        );

        uploadingPointsGeo.forEach((coord) => bounds.extend(coord));
        unloadingPointsGeo.forEach((coord) => bounds.extend(coord));

        map.current.fitBounds(bounds, { padding: 30 });
    }, [setDirectionInfo, originPoint, destinationPoint, mapboxgl, map, uploadingPointsGeo, unloadingPointsGeo]);

    useEffect(() => {
        if (!directions) {
            const dir = new MapboxDirections({
                accessToken: mapboxgl.accessToken,
                unit: "metric",
                profile: "mapbox/driving",
                interactive: false,
                controls: false,
                styles: directionStyles,
            });
            dir.on("route", handleDirectionRoute);
            setDirections(dir);
        }
    }, [mapboxgl.accessToken, handleDirectionRoute, directions]);

    useEffect(() => {
        if (map.current) {
            map.current.on("load", handleMapLoad);
        }
    }, [map, handleMapLoad]);

    return null;
};

export default Direction;
